import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import Tooltip from '@mui/material/Tooltip'
import useStore from 'reusable/mobx-store-utils'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import { SxProps, Theme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import { themeConfig } from '@gap/shared/theme'
import { useGapDialogStore } from './GapDialogStore'

export type GapDialogActionType = 'primary' | 'secondary' | 'cancel'
interface ButtonProps extends Pick<React.ComponentProps<typeof Button>, 'variant'> {
   sx: SxProps<Theme>,
   'data-testid': string,
}

export interface GapDialogAction {
   /** action name (will appear on button) */
   name: string | React.ReactNode
   /** action title (will be a tooltip) */
   title?: string
   /** action itself (what to execute if the user clicks the button) */
   callback?: (params: {
      /** so that the action has a way of closing the dialog if successful */
      closeDialog: () => any
   }) => any
   /** if set to true, clicking on this action won't make the dialog close */
   notCloseAutomatically?: boolean
   /** if the button is disabled */
   disabled?: boolean
   /** if the button is loading */
   loading?: boolean
   /** Button variant */
   variant?: 'normal' | 'destroy'
   /** icon to show at the end of the button */
   endIcon?: React.ReactNode
   /** if set, the button takes full width. If both primary and secondary are fullWidth, they each take 50% */
   fullWidth?: boolean
}

interface GapDialogActionProps {
   action: GapDialogAction
   variant: GapDialogActionType
   sx?: SxProps<Theme>
}

export type DialogButtonOptions = { [key in GapDialogActionType]?: GapDialogAction }

export default observer(function GapDialogActionButton(props: GapDialogActionProps) {
   const gapDialogStore = useGapDialogStore()

   const localStore = useStore(sp => ({
      handleClose: () => {
         gapDialogStore.handleClose(sp.action)
      },
   }), { action: props.action })

   const buttonProps: ButtonProps = useMemo(() => {
      switch (props.variant) {
         case 'primary':
            return {
               sx: {
                  backgroundColor: props.action.variant === 'destroy' ? themeConfig.destroyButtonColor : themeConfig.primary['800'],
                  color: 'white',
                  ...(!props.action.fullWidth && { mr: 2 }),
               },
               disabled: props.action.disabled,
               variant: 'contained',
               'data-testid': 'dialog-primary-button'
            }
         case 'secondary':
            return {
               sx: {
                  color: themeConfig.primary['800'],
                  borderColor: `${themeConfig.primary['800']}50`,
                  ...props.action.variant === 'destroy' ? {
                     backgroundColor: themeConfig.destroyButtonColor,
                     color: 'white !important',
                  } : {}
               },
               disabled: props.action.disabled,
               variant: 'outlined',
               'data-testid': 'dialog-secondary-button'
            }
         case 'cancel':
            return {
               variant: 'text',
               sx: {
                  color: themeConfig.primary['800']
               },
               disabled: props.action.disabled,
               'data-testid': 'dialog-cancel-button'
            }
      }
   }, [props.action.variant, props.action.fullWidth, props.action.disabled, props.variant])

   const mergedSx = props.sx ? [buttonProps.sx, props.sx] as SxProps<Theme> : buttonProps.sx
   const button = <ButtonWithProgress onClick={localStore.handleClose} {...buttonProps} sx={mergedSx} loading={props.action.loading} {...props.action.endIcon ? { icon: props.action.endIcon } : {}}>
      {props.action.name}
   </ButtonWithProgress>

   return !!props.action?.title ? <Tooltip title={props.action.title}>{button}</Tooltip> : button
})
